import { createContext, useState, useEffect, useContext, useRef } from "react";
import { useAuth } from "./auth";
import { isTrialActive, getTrialTimeRemaining, isBoardInTrial } from "../utilities/trialConfig";
import toastService from "../services/toastService";

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:8080';

// Premium 3D Boards available in the store
export const PREMIUM_BOARDS = [
  {
    id: "inferno",
    name: "Blaze",
    skinName: "Blaze",
    description: "",
    color: "red",
    thumbnail: "inferno.png",
    isFree: true,
    boardItemName: "Blaze Board", // DB item name for ownership check
    buyUrl: "https://errorrunner.gumroad.com/l/ccrch?wanted=true"
  },
  {
    id: "spectrum",
    name: "Spectrum",
    skinName: "Spectrum",
    description: "",
    color: "rainbow",
    thumbnail: "spectrum.png",
    isFree: false,
    boardItemName: "Spectrum Board",
    buyUrl: "https://errorrunner.gumroad.com/l/emlchk?wanted=true"
  },
  {
    id: "frost",
    name: "Frost",
    skinName: "Cyan",
    description: "",
    color: "blue",
    thumbnail: "frost.png",
    isFree: false,
    boardItemName: "Frost Board",
    buyUrl: "https://errorrunner.gumroad.com/l/bahat?wanted=true"
  },
  {
    id: "none",
    name: "Default",
    skinName: "Default",
    description: "",
    color: "gray",
    thumbnail: "default.png",
    isFree: true
  },
];

// Map UI themes to 3D skin names (legacy - now overridden by premium board selection)
const THEME_TO_SKIN_MAP = {
  'theme-4': 'Cyan',   // Frost theme uses Cyan 3D skin
  'theme-3': 'Spectrum',  // Nova theme uses Spectrum 3D skin
  'theme-0': 'Blaze',  // Blaze theme uses Blaze 3D skin
  '4': 'Cyan',
  '3': 'Spectrum',
  '0': 'Blaze',
};

export const getSkinFromTheme = (theme) => {
  if (!theme) return null; // Default to 2D
  const themeStr = typeof theme === 'string' ? theme : `theme-${theme}`;
  // Only return 3D skin for mapped themes, otherwise null for 2D
  return THEME_TO_SKIN_MAP[themeStr] || THEME_TO_SKIN_MAP[theme] || null;
};

export const BoardSkinContext = createContext(null);

export const BoardSkinProvider = ({ children }) => {
  const { user, isAuthenticated, themeData: preloadedThemeData, itemsData: preloadedItemsData } = useAuth();

  // Default to "none" — synced from DB via useEffect below
  const [selectedPremiumBoard, setSelectedPremiumBoard] = useState("none");
  const [ownedBoardIds, setOwnedBoardIds] = useState([]); // Array of board ID strings (e.g., ["inferno", "frost"])
  const [userCreatedAt, setUserCreatedAt] = useState(null);
  const trialTimerRef = useRef(null);

  // Derived trial state
  const trialActive = isAuthenticated && isTrialActive(userCreatedAt);

  // Derive skinName from selected premium board
  const [skinName, setSkinName] = useState(() => {
    const board = PREMIUM_BOARDS.find(b => b.id === selectedPremiumBoard);
    return board ? board.skinName : null;
  });

  // Sync with database when authenticated and theme data is loaded
  useEffect(() => {
    if (isAuthenticated && preloadedThemeData) {
      // Set selected board (already validated by backend)
      const boardItem = preloadedThemeData.user?.selectedBoardSkin;
      if (boardItem) {
        // Map Item object to board ID
        const ITEM_NAME_TO_BOARD_ID = {
          "Blaze Board": "inferno",
          "Frost Board": "frost",
          "Spectrum Board": "spectrum",
        };
        const boardId = ITEM_NAME_TO_BOARD_ID[boardItem.name] || "none";
        setSelectedPremiumBoard(boardId);
      } else {
        setSelectedPremiumBoard("inferno"); // Default to Blaze Board
      }

      // Extract trialStart for trial calculation
      if (preloadedThemeData.user?.trialStart) {
        setUserCreatedAt(preloadedThemeData.user.trialStart);
      }

      // Extract owned board IDs from /api/user/items
      const owned = [];
      if (preloadedItemsData) {
        const boardItems = preloadedItemsData.filter(item => item.type === 'BOARD_SKIN');
        for (const boardItem of boardItems) {
          if (boardItem.isOwned) {
            const board = PREMIUM_BOARDS.find(b => b.boardItemName === boardItem.name);
            if (board) owned.push(board.id);
          }
        }
      }
      setOwnedBoardIds(owned);
    } else if (isAuthenticated && !preloadedThemeData) {
      // Logged in but /me failed — fall back to defaults
      setSelectedPremiumBoard("inferno"); // Default to Blaze Board
      setOwnedBoardIds([]);
      setUserCreatedAt(null);
    } else if (!isAuthenticated) {
      setSelectedPremiumBoard("inferno"); // Default to Blaze Board for guests
      setOwnedBoardIds([]);
      setUserCreatedAt(null);
    }
  }, [isAuthenticated, preloadedThemeData, preloadedItemsData]);

  // Update skinName when premium board selection changes
  useEffect(() => {
    const board = PREMIUM_BOARDS.find(b => b.id === selectedPremiumBoard);
    setSkinName(board ? board.skinName : null);
  }, [selectedPremiumBoard]);

  // No validation needed on load - backend already validates in /api/user/me
  // Validation only happens when trial timer expires (see below)

  // Trial expiry timer — reset board when trial ends
  useEffect(() => {
    if (trialTimerRef.current) {
      clearTimeout(trialTimerRef.current);
      trialTimerRef.current = null;
    }

    if (!trialActive || !userCreatedAt) return;

    const remaining = getTrialTimeRemaining(userCreatedAt);
    if (remaining <= 0) return;

    trialTimerRef.current = setTimeout(() => {
      // Trial just expired — check if current board was trial-only
      if (selectedPremiumBoard !== 'none' && !ownedBoardIds.includes(selectedPremiumBoard) && isBoardInTrial(selectedPremiumBoard)) {
        setSelectedPremiumBoard('inferno');
        toastService.show("Trial ended", "Your free trial has expired. Board reset to Blaze.", "info");
      }
    }, remaining);

    return () => {
      if (trialTimerRef.current) {
        clearTimeout(trialTimerRef.current);
      }
    };
  }, [trialActive, userCreatedAt, selectedPremiumBoard, ownedBoardIds]);

  // Set premium board and persist to database
  const setPremiumBoard = async (boardId) => {
    const board = PREMIUM_BOARDS.find(b => b.id === boardId);
    if (!board) return;

    // Validate ownership for non-free boards
    if (!board.isFree && boardId !== 'none') {
      const isOwned = ownedBoardIds.includes(boardId);
      const hasTrialAccess = trialActive && isBoardInTrial(boardId);

      if (!isOwned && !hasTrialAccess) {
        toastService.show("Board not owned", "Please purchase this board skin first.", "error");
        return;
      }
    }

    const previousBoard = selectedPremiumBoard;
    setSelectedPremiumBoard(boardId);

    if (isAuthenticated && user) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/boardskin`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ boardId })
        });

        if (!response.ok) {
          // Server rejected — revert
          setSelectedPremiumBoard(previousBoard);
          toastService.show("Failed to apply board skin", "Server rejected the change.", "error");
          return false;
        }
      } catch (error) {
        setSelectedPremiumBoard(previousBoard);
        toastService.show("Failed to apply board skin", "Could not save to server.", "error");
        return false;
      }
    }
    return true;
  };

  // Manual skin override (legacy - kept for compatibility)
  const changeSkin = (newSkinName) => {
    setSkinName(newSkinName);
  };

  // Available skins
  const AVAILABLE_SKINS = [
    {
      id: "spectrum",
      name: "Spectrum",
      description: "Valorant-inspired metallic skin with particle effects",
      themeId: 3, // Maps to UI theme-3 (Nova/Yellow)
      colors: {
        primary: "#3A4A5A",
        secondary: "#FFFFFF",
        emissive: "#4A5A6A",
        background: "#0a0a12"
      }
    },
    {
      id: "cyan",
      name: "Cyan",
      description: "Ice-themed crystalline skin with frost effects",
      themeId: 4, // Maps to UI theme-4 (Frost/Cyan)
      colors: {
        primary: "#40E0D0",
        secondary: "#E0FFFF",
        emissive: "#00CED1",
        background: "#0a1520"
      }
    },
    {
      id: "blaze",
      name: "Blaze",
      description: "Fire-themed ember skin with flame effects",
      themeId: 0, // Maps to UI theme-0 (Blaze/Red)
      colors: {
        primary: "#FF4500",
        secondary: "#FFD700",
        emissive: "#FF6600",
        background: "#0f0800"
      }
    }
  ];

  const syncOwnedBoardsFromItems = (items) => {
    const boardItems = items?.filter(item => item.type === 'BOARD_SKIN') || [];
    const owned = [];
    for (const boardItem of boardItems) {
      if (boardItem.isOwned) {
        const board = PREMIUM_BOARDS.find(b => b.boardItemName === boardItem.name);
        if (board) owned.push(board.id);
      }
    }
    setOwnedBoardIds(owned);
  };

  const contextValue = {
    // Current skin (null = 2D, "Spectrum" = 3D)
    skinName,

    // Function to change skin (legacy)
    setSkin: changeSkin,

    // Get current skin config
    currentSkinConfig: AVAILABLE_SKINS.find(s => s.name === skinName) || null,

    // All available skins
    availableSkins: AVAILABLE_SKINS,

    // Premium boards
    premiumBoards: PREMIUM_BOARDS,
    selectedPremiumBoard,
    setPremiumBoard,

    // Ownership
    ownedBoardIds,
    trialActive,
    syncOwnedBoardsFromItems,
  };

  return (
    <BoardSkinContext.Provider value={contextValue}>
      {children}
    </BoardSkinContext.Provider>
  );
};

export const useBoardSkin = () => {
  const context = useContext(BoardSkinContext);
  if (!context) {
    throw new Error("useBoardSkin must be used within BoardSkinProvider");
  }
  return context;
};
